import React, { useState } from "react";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { WHATSAPP_URL } from "@/lib/site";

export default function QuoteForm({ source = "site", subjectDefault = "" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: subjectDefault,
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSending(true);
    try {
      const message = [
        "Olá! Gostaria de solicitar um orçamento.",
        "",
        `Nome: ${form.name}`,
        `E-mail: ${form.email}`,
        `Telefone/WhatsApp: ${form.phone}`,
        form.company ? `Empresa: ${form.company}` : null,
        form.subject ? `Assunto: ${form.subject}` : null,
        "",
        `Mensagem: ${form.message}`,
        "",
        `Origem: ${source}`,
      ]
        .filter(Boolean)
        .join("\n");

      window.open(WHATSAPP_URL(message), "_blank", "noopener,noreferrer");
      setDone(true);
      setForm({ name: "", email: "", phone: "", company: "", subject: subjectDefault, message: "" });
      toast.success("Abrimos o WhatsApp com sua solicitação.");
    } catch (e2) {
      const msg = e2?.message || "Não foi possível abrir o WhatsApp. Tente novamente.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-form-success"
        className="bg-white border border-surface-line rounded-sm p-8 text-center"
      >
        <CheckCircle2 className="h-10 w-10 text-brand mx-auto" />
        <h3 className="mt-4 font-display text-xl font-bold text-ink">
          Solicitação recebida
        </h3>
        <p className="mt-2 text-sm text-ink-muted max-w-md mx-auto">
          Nosso time comercial entrará em contato em até 24 horas úteis com
          informações técnicas e proposta comercial.
        </p>
        <button
          type="button"
          onClick={() => setDone(false)}
          data-testid="quote-form-new"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-mid"
        >
          Enviar nova solicitação <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-sm border border-surface-line bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-brand-mid focus:border-brand-mid transition-all";

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="bg-white border border-surface-line rounded-sm p-6 sm:p-8 shadow-sm"
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Nome*
          </label>
          <input
            name="name"
            required
            value={form.name}
            onChange={onChange}
            data-testid="qf-name"
            className={inputCls + " mt-1"}
            placeholder="Seu nome"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            E-mail corporativo*
          </label>
          <input
            name="email"
            type="email"
            required
            value={form.email}
            onChange={onChange}
            data-testid="qf-email"
            className={inputCls + " mt-1"}
            placeholder="nome@empresa.com.br"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Telefone / WhatsApp*
          </label>
          <input
            name="phone"
            required
            value={form.phone}
            onChange={onChange}
            data-testid="qf-phone"
            className={inputCls + " mt-1"}
            placeholder="+55 (00) 00000-0000"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Empresa
          </label>
          <input
            name="company"
            value={form.company}
            onChange={onChange}
            data-testid="qf-company"
            className={inputCls + " mt-1"}
            placeholder="Razão social"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Assunto
          </label>
          <input
            name="subject"
            value={form.subject}
            onChange={onChange}
            data-testid="qf-subject"
            className={inputCls + " mt-1"}
            placeholder="Ex.: Cotação spare parts BOP"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Mensagem / Necessidade*
          </label>
          <textarea
            name="message"
            required
            rows={4}
            value={form.message}
            onChange={onChange}
            data-testid="qf-message"
            className={inputCls + " mt-1 resize-none"}
            placeholder="Descreva o item, aplicação, prazo e quantidade."
          />
        </div>
      </div>

      {err && (
        <p data-testid="qf-error" className="mt-4 text-sm text-destructive">
          {err}
        </p>
      )}

      <button
        type="submit"
        disabled={sending}
        data-testid="qf-submit"
        className="mt-6 inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-brand text-white font-semibold px-8 py-3.5 rounded-sm hover:bg-brand-mid transition-colors disabled:opacity-60 border border-transparent hover:border-bronze"
      >
        {sending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
          </>
        ) : (
          <>
            Solicitar orçamento <ArrowRight className="h-4 w-4" />
          </>
        )}
      </button>
      <p className="mt-3 text-xs text-ink-muted">
        Retorno em até 24h úteis. Seus dados são tratados com confidencialidade.
      </p>
    </form>
  );
}
